import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, X } from "lucide-react";

/** Quick picks; 7 is the standard "we couldn't seat you" compensation. */
const PRESET_DAYS = [7, 14, 30];
const MIN_DAYS = 1;
const MAX_DAYS = 365;

interface ExtendSubscriptionModalProps {
  user: { firstName: string; lastName: string | null };
  /** Current renewal date of the Stripe subscription, if known. */
  currentRenewalAt?: Date | string | null;
  onClose: () => void;
  onConfirm: (days: number) => void;
  isExtending: boolean;
}

/**
 * Days-to-extend picker for pushing a Stripe subscriber's next charge out.
 * Extensions stack, so this always describes a delta on top of whatever the
 * renewal date already is.
 */
export function ExtendSubscriptionModal({
  user,
  currentRenewalAt,
  onClose,
  onConfirm,
  isExtending,
}: ExtendSubscriptionModalProps) {
  const [days, setDays] = useState(7);

  const isValid = Number.isInteger(days) && days >= MIN_DAYS && days <= MAX_DAYS;

  const currentRenewal = currentRenewalAt ? new Date(currentRenewalAt) : null;
  const projectedRenewal =
    currentRenewal && isValid
      ? new Date(currentRenewal.getTime() + days * 24 * 60 * 60 * 1000)
      : null;

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", { dateStyle: "medium" });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="card border border-dark-border rounded-xl max-w-md w-full p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-500/20">
              <CalendarClock className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Extend Subscription</h3>
              <p className="text-dark-muted text-sm">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isExtending}
            className="text-dark-muted hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-dark-muted text-sm mb-5">
          Pushes their next Stripe charge out by the selected number of days.
          Extensions stack, so this adds to any previous one. Only works for
          Stripe subscriptions: for App Store or Play Store subscribers, grant a
          one-time entitlement instead.
        </p>

        <label className="block text-sm text-dark-muted mb-2">Days to add</label>
        <div className="flex gap-2 mb-3">
          {PRESET_DAYS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setDays(preset)}
              disabled={isExtending}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
                days === preset
                  ? "bg-primary-500/20 text-primary-400 border-primary-500"
                  : "bg-dark-border/50 text-dark-muted border-dark-border hover:text-white"
              }`}
            >
              {preset} days
            </button>
          ))}
        </div>

        <input
          type="number"
          min={MIN_DAYS}
          max={MAX_DAYS}
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          disabled={isExtending}
          className="w-full px-4 py-3 bg-dark-border/50 border border-dark-border rounded-lg text-sm focus:outline-none focus:border-primary-500 transition-colors mb-4"
        />

        {currentRenewal && (
          <div className="bg-dark-bg/50 rounded-lg p-4 mb-5 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-dark-muted">Renews now:</span>
              <span className="font-medium">{formatDate(currentRenewal)}</span>
            </div>
            {projectedRenewal && (
              <div className="flex justify-between text-sm">
                <span className="text-dark-muted">Renews after:</span>
                <span className="font-medium text-primary-400">
                  {formatDate(projectedRenewal)}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isExtending}
            className="flex-1 px-4 py-3 bg-dark-border/50 rounded-lg text-sm font-medium hover:bg-dark-border transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(days)}
            disabled={!isValid || isExtending}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExtending ? "Extending..." : `Extend ${isValid ? days : ""} days`}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
